import React from 'react';
import { Badge, Box, Flex, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Eyebrow } from './primitives';
import { ResultBlockRow } from './types';
import { scoreExpectation, weightedMean } from './score';
import { overallFirst } from './helpers';
import { BlockDelta, RoundComparisonView } from './BlockDelta';

const UNNAMED_BLOCK_KEY = '__unnamed__';
const MAX_SCORE = 5;

interface PublishedSummaryBodyProps {
  blocks: Array<{ block?: string; comment?: string }>;
  // Per-block averages from /results. Blocks are scored even when the admin left
  // no comment on them, and commented even when nobody scored them, so the two
  // lists are merged by block name rather than zipped.
  scores?: ResultBlockRow[];
  // Free-text summary kept by legacy rounds that predate per-block summaries.
  legacyText?: string;
  publishedAt?: string | Date;
  locale: string;
  /** Динамика к прошлому раунду; приходит из того же /results. */
  comparison?: RoundComparisonView | null;
}

// Цвет по умолчанию — фирменный: у блочных полос он и был таким, менять их
// сейчас незачем. Итогу цвет передаётся явно: его число и подпись уже окрашены
// вердиктом, и зелёная полоса под красной оценкой спорила бы сама с собой.
const ScoreBar: React.FC<{ score: number; color?: string }> = ({ score, color }) => (
  <Box
    style={{
      position: 'relative',
      height: 6,
      background: 'var(--mantine-color-default-border)',
      borderRadius: 999,
      overflow: 'hidden',
    }}
  >
    <Box
      style={{
        position: 'absolute',
        inset: 0,
        width: `${(score / MAX_SCORE) * 100}%`,
        background: color
          ? `var(--mantine-color-${color}-filled)`
          : 'var(--mantine-primary-color-filled)',
        borderRadius: 999,
        transition: 'width 600ms cubic-bezier(.2,.8,.2,1)',
      }}
    />
  </Box>
);

export const PublishedSummaryBody: React.FC<PublishedSummaryBodyProps> = ({
  blocks,
  scores,
  legacyText,
  publishedAt,
  locale,
  comparison,
}) => {
  const { t } = useTranslation();
  const expDefaults = t('perfReview.expectationScale', { returnObjects: true });
  const expLabels = Array.isArray(expDefaults) ? (expDefaults as string[]) : undefined;
  const publishedLabel = publishedAt
    ? new Date(publishedAt).toLocaleDateString(locale, { day: 'numeric', month: 'long' })
    : null;

  const rows = new Map<
    string,
    { block: string; comment?: string; score?: number; count?: number }
  >();
  blocks
    .filter((b) => b.block && b.comment?.trim())
    .forEach((b) => rows.set(b.block!, { block: b.block!, comment: b.comment }));
  (scores || [])
    .filter((s) => s.block && typeof s.scoreMean === 'number')
    .forEach((s) => {
      const row = rows.get(s.block!) ?? { block: s.block! };
      rows.set(s.block!, { ...row, score: s.scoreMean, count: s.responseCount });
    });
  const merged = overallFirst(Array.from(rows.values()));
  const deltaByBlock = new Map((comparison?.blocks ?? []).map((r) => [r.block, r]));

  // Итог по всему ревью. Считаем из тех же `scores`, что и экран результатов, и
  // той же функцией: человек и его лид не должны видеть под одним заголовком
  // два разных числа. Дельты здесь намеренно нет — она есть у каждого блока
  // ниже, а взвешенному среднему сравнивать не с чем: прошлый итог в ответе не
  // приходит.
  const total = weightedMean(scores || []);
  const totalMeta = total ? scoreExpectation(total.mean, expLabels) : null;

  return (
    <Paper withBorder p="xl" radius="md">
      <Flex justify="space-between" align="flex-end" mb="lg" gap="md">
        <Box>
          <Eyebrow>{t('perfReview.home.publishedSummary.section')}</Eyebrow>
          <Title order={3} size="h4" mt={6} style={{ letterSpacing: '-0.01em' }}>
            {t('perfReview.home.publishedSummary.title')}
          </Title>
        </Box>
        {publishedLabel && (
          <Badge
            variant="light"
            color="teal"
            size="sm"
            leftSection={<IconCircleCheck size={12} />}
            style={{ flexShrink: 0 }}
          >
            {t('perfReview.home.publishedSummary.publishedOn', { date: publishedLabel })}
          </Badge>
        )}
      </Flex>

      {comparison?.professionChanged && (
        <Text size="sm" c="dimmed" mb="md">
          {t('perfReview.dynamics.professionChanged')}
        </Text>
      )}

      {/* Итог стоит над блоками и набран крупнее: это вывод по всему ревью, а
          не ещё одна строка наравне с частными темами. */}
      {total && totalMeta && (
        <Box
          mb="xl"
          pb="lg"
          style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
        >
          <Flex justify="space-between" align="baseline" gap="sm" mb={8}>
            <Text size="sm" fw={600}>
              {t('perfReview.overallScore')}
            </Text>
            <Group gap={8} align="baseline" wrap="nowrap" style={{ flexShrink: 0 }}>
              <Text
                size="xl"
                fw={700}
                c={totalMeta.color}
                style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}
              >
                {total.mean.toFixed(1)}
              </Text>
              {totalMeta.label && (
                <Text size="xs" fw={600} c={totalMeta.color}>
                  {totalMeta.label}
                </Text>
              )}
            </Group>
          </Flex>
          <ScoreBar score={total.mean} color={totalMeta.color} />
          <Text size="xs" c="dimmed" mt={8}>
            {t('perfReview.overallScoreHint', { count: total.count })}
          </Text>
        </Box>
      )}

      {merged.length ? (
        <Stack gap="lg">
          {merged.map((r) => (
            <Box key={r.block}>
              <Flex justify="space-between" align="baseline" gap="sm" mb={6}>
                <Text size="sm" fw={600}>
                  {r.block === UNNAMED_BLOCK_KEY ? t('perfReview.unnamedSection') : r.block}
                </Text>
                {typeof r.score === 'number' &&
                  (() => {
                    const meta = scoreExpectation(r.score, expLabels);
                    return (
                      <Group gap={8} align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
                        <Badge
                          variant="light"
                          color={meta.color}
                          radius="sm"
                          size="md"
                          style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                        >
                          {r.score.toFixed(1)}/{MAX_SCORE}
                          {meta.label ? ` · ${meta.label}` : ''}
                        </Badge>
                        {/* Счётчик показываем один раз: когда есть динамика, она
                            несёт оба числа — прошлого раунда и текущего. */}
                        {r.count && !deltaByBlock.has(r.block) ? (
                          <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {t('perfReview.home.publishedSummary.responses', { count: r.count })}
                          </Text>
                        ) : null}
                      </Group>
                    );
                  })()}
              </Flex>
              {/* Отдельной строкой, а не в шапке блока: колонка здесь узкая, и
                  вместе с бейджем оценки аннотация отжимала название блока на
                  две-три строки. Название важнее. */}
              {deltaByBlock.has(r.block) && (
                <Box mb={6}>
                  <BlockDelta row={deltaByBlock.get(r.block)} />
                </Box>
              )}
              {typeof r.score === 'number' && <ScoreBar score={r.score} />}
              {r.comment && (
                <Text size="sm" lh={1.6} mt={10} style={{ whiteSpace: 'pre-wrap' }}>
                  {r.comment}
                </Text>
              )}
            </Box>
          ))}
        </Stack>
      ) : (
        <Text size="sm" lh={1.6} style={{ whiteSpace: 'pre-wrap' }}>
          {legacyText}
        </Text>
      )}
    </Paper>
  );
};
